import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'
import {
  useLazyGetActiveUserNotificationsQuery,
  useLogUserNotificationActionMutation,
} from '../../../services/UserNotificationApi'
import {
  getUserNotificationConnectionId,
  startUserNotificationsHub,
  stopUserNotificationsHub,
  subscribeUserNotifications,
  UserNotificationPayload,
} from '../../../services/userNotificationsHub'
import {
  ActiveUserNotification,
  formatCountdown,
  getNotificationSessionId,
  USER_NOTIFICATION_ACTION,
  USER_NOTIFICATION_DISPLAY_MODE,
  USER_NOTIFICATION_TYPE,
} from './userNotificationTypes'

type Ctx = {
  maintenance: ActiveUserNotification | null
  generalBanner: ActiveUserNotification | null
  generalPopup: ActiveUserNotification | null
  dismissBanner: (id: number) => void
  dismissPopup: (id: number, auto?: boolean) => void
}

const UserNotificationContext = createContext<Ctx>({
  maintenance: null,
  generalBanner: null,
  generalPopup: null,
  dismissBanner: () => undefined,
  dismissPopup: () => undefined,
})

export const useUserNotifications = () => useContext(UserNotificationContext)

const toActive = (p: UserNotificationPayload | ActiveUserNotification): ActiveUserNotification => ({
  notificationId: p.notificationId,
  organizationId: p.organizationId,
  notificationTypeId: p.notificationTypeId,
  notificationType: p.notificationType,
  title: p.title,
  messageHtml: p.messageHtml,
  displayModeId: p.displayModeId,
  displayMode: p.displayMode,
  startDateTimeUtc: p.startDateTimeUtc,
  endDateTimeUtc: p.endDateTimeUtc,
  logoutDeadlineUtc: p.logoutDeadlineUtc,
  remainingLogoutSeconds: (p as any).remainingLogoutSeconds,
  popupDisplaySeconds: p.popupDisplaySeconds,
  priority: p.priority ?? 99,
  status: (p as any).status,
  audienceTypeId: (p as any).audienceTypeId,
  inAppEnabled: (p as any).inAppEnabled !== false,
  popupEnabled:
    (p as any).popupEnabled === true ||
    p.displayModeId === USER_NOTIFICATION_DISPLAY_MODE.Popup ||
    p.notificationTypeId === USER_NOTIFICATION_TYPE.MaintenanceWindow,
  pushEnabled: !!(p as any).pushEnabled,
  forceLogoutEnabled:
    (p as any).forceLogoutEnabled === true ||
    p.notificationTypeId === USER_NOTIFICATION_TYPE.MaintenanceWindow,
  forceLogoutSeconds: (p as any).forceLogoutSeconds,
})

export const UserNotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token, baseURL, userAccountID, userInOrgId } = useAppSelector(selectAuth)
  const navigate = useNavigate()
  const [items, setItems] = useState<ActiveUserNotification[]>([])
  const [dismissedIds, setDismissedIds] = useState<number[]>([])
  const [countdown, setCountdown] = useState<number | null>(null)
  const loggedActions = useRef<Set<string>>(new Set())
  const [fetchActive] = useLazyGetActiveUserNotificationsQuery()
  const [logAction] = useLogUserNotificationActionMutation()

  const track = useCallback(
    async (id: number, actionTypeId: number) => {
      const key = `${id}:${actionTypeId}:${getNotificationSessionId()}`
      if (loggedActions.current.has(key)) return
      loggedActions.current.add(key)
      try {
        await logAction({
          id,
          actionTypeId,
          sessionId: getNotificationSessionId(),
          signalRConnectionId: getUserNotificationConnectionId(),
          pageUrl: window.location.pathname,
        }).unwrap()
      } catch {
        loggedActions.current.delete(key)
      }
    },
    [logAction]
  )

  const applyList = useCallback((list: ActiveUserNotification[]) => {
    const sorted = [...list].sort((a, b) => (a.priority || 99) - (b.priority || 99))
    setItems(sorted)
  }, [])

  const syncActive = useCallback(async () => {
    if (!token || !baseURL) return
    try {
      const res: any = await fetchActive(undefined as any).unwrap()
      const list = (res?.result || []) as ActiveUserNotification[]
      applyList(Array.isArray(list) ? list.map(toActive) : [])
    } catch {
      /* ignore */
    }
  }, [token, baseURL, fetchActive, applyList])

  useEffect(() => {
    if (!token || !baseURL || !userAccountID) {
      stopUserNotificationsHub()
      setItems([])
      return
    }

    let cancelled = false
    ;(async () => {
      if (cancelled) return
      await startUserNotificationsHub({
        baseURL,
        token,
        orgId: userInOrgId,
        orgUserId: userAccountID,
      })
      if (!cancelled) await syncActive()
    })()

    const unsub = subscribeUserNotifications((eventType, payload) => {
      if (eventType === '__reconnected__') {
        syncActive()
        return
      }
      if (eventType === 'NotificationCancelled' || eventType === 'NotificationEnded') {
        setItems((prev) => prev.filter((x) => x.notificationId !== payload.notificationId))
        setDismissedIds((prev) => prev.filter((id) => id !== payload.notificationId))
        return
      }
      if (
        eventType === 'NotificationStarted' ||
        eventType === 'NotificationUpdated' ||
        eventType === 'MaintenanceLogoutWarning'
      ) {
        const next = toActive(payload)
        setItems((prev) => {
          const others = prev.filter((x) => x.notificationId !== next.notificationId)
          return [...others, next].sort((a, b) => (a.priority || 99) - (b.priority || 99))
        })
        track(next.notificationId, USER_NOTIFICATION_ACTION.NotificationReceived)
      }
    })

    return () => {
      cancelled = true
      unsub()
      stopUserNotificationsHub()
    }
  }, [token, baseURL, userAccountID, userInOrgId, syncActive, track])

  const maintenance = useMemo(
    () =>
      items.find((x) => x.notificationTypeId === USER_NOTIFICATION_TYPE.MaintenanceWindow) || null,
    [items]
  )

  const generalBanner = useMemo(
    () =>
      items.find(
        (x) =>
          x.notificationTypeId === USER_NOTIFICATION_TYPE.GeneralNotification &&
          (x.inAppEnabled !== false ||
            x.displayModeId === USER_NOTIFICATION_DISPLAY_MODE.FixedBanner) &&
          !dismissedIds.includes(x.notificationId)
      ) || null,
    [items, dismissedIds]
  )

  const generalPopup = useMemo(() => {
    if (maintenance) return null
    return (
      items.find(
        (x) =>
          x.notificationTypeId === USER_NOTIFICATION_TYPE.GeneralNotification &&
          (x.popupEnabled === true ||
            x.displayModeId === USER_NOTIFICATION_DISPLAY_MODE.Popup) &&
          !dismissedIds.includes(x.notificationId)
      ) || null
    )
  }, [items, dismissedIds, maintenance])

  useEffect(() => {
    if (!maintenance || maintenance.forceLogoutEnabled === false) {
      if (!maintenance) setCountdown(null)
      return
    }
    track(maintenance.notificationId, USER_NOTIFICATION_ACTION.BannerDisplayed)
    if (maintenance.popupEnabled !== false) {
      track(maintenance.notificationId, USER_NOTIFICATION_ACTION.PopupDisplayed)
    }
    track(maintenance.notificationId, USER_NOTIFICATION_ACTION.LogoutCountdownStarted)

    const deadlineMs = maintenance.logoutDeadlineUtc
      ? new Date(maintenance.logoutDeadlineUtc).getTime()
      : Date.now() + (maintenance.remainingLogoutSeconds || 0) * 1000

    const tick = () => {
      const remaining = Math.ceil((deadlineMs - Date.now()) / 1000)
      if (remaining <= 0) {
        setCountdown(0)
        track(maintenance.notificationId, USER_NOTIFICATION_ACTION.UserForceLoggedOut)
        navigate('/logout?reason=maintenance')
        return
      }
      setCountdown(remaining)
    }
    tick()
    const timer = window.setInterval(tick, 1000)
    return () => window.clearInterval(timer)
  }, [maintenance, navigate, track])

  useEffect(() => {
    if (!generalBanner) return
    track(generalBanner.notificationId, USER_NOTIFICATION_ACTION.BannerDisplayed)
  }, [generalBanner, track])

  useEffect(() => {
    if (!generalPopup) return
    track(generalPopup.notificationId, USER_NOTIFICATION_ACTION.PopupDisplayed)
    if (!generalPopup.popupDisplaySeconds || generalPopup.popupDisplaySeconds <= 0) return
    const timer = window.setTimeout(() => {
      setDismissedIds((prev) =>
        prev.includes(generalPopup.notificationId) ? prev : [...prev, generalPopup.notificationId]
      )
      track(generalPopup.notificationId, USER_NOTIFICATION_ACTION.NotificationAutoClosed)
    }, generalPopup.popupDisplaySeconds * 1000)
    return () => window.clearTimeout(timer)
  }, [generalPopup, track])

  const dismissBanner = (id: number) => {
    setDismissedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    track(id, USER_NOTIFICATION_ACTION.BannerClosed)
  }

  const dismissPopup = (id: number, auto = false) => {
    setDismissedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
    track(id, auto ? USER_NOTIFICATION_ACTION.NotificationAutoClosed : USER_NOTIFICATION_ACTION.PopupClosed)
  }

  const value: Ctx = {
    maintenance,
    generalBanner,
    generalPopup,
    dismissBanner,
    dismissPopup,
  }

  return (
    <UserNotificationContext.Provider value={value}>
      {children}

      {generalBanner && (
        <div
          className='alert alert-info d-flex align-items-start justify-content-between mb-0 rounded-0 position-fixed top-0 start-0 end-0'
          style={{ zIndex: 1055 }}
        >
          <div className='me-3 container-fluid'>
            <div className='fw-bold'>{generalBanner.title}</div>
            <div dangerouslySetInnerHTML={{ __html: generalBanner.messageHtml }} />
          </div>
          <button
            type='button'
            className='btn-close m-3'
            aria-label='Close'
            onClick={() => dismissBanner(generalBanner.notificationId)}
          />
        </div>
      )}

      <Modal
        show={!!maintenance && maintenance.popupEnabled !== false}
        backdrop='static'
        keyboard={false}
        centered
      >
        <Modal.Header>
          <Modal.Title>{maintenance?.title || 'Scheduled Maintenance'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div dangerouslySetInnerHTML={{ __html: maintenance?.messageHtml || '' }} />
          <p className='mt-4 mb-1 fw-semibold'>You will be logged out automatically in:</p>
          <div className='display-6 text-danger fw-bold'>{formatCountdown(countdown ?? 0)}</div>
        </Modal.Body>
      </Modal>

      <Modal
        show={!!generalPopup}
        onHide={() => generalPopup && dismissPopup(generalPopup.notificationId)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{generalPopup?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div dangerouslySetInnerHTML={{ __html: generalPopup?.messageHtml || '' }} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='primary'
            onClick={() => generalPopup && dismissPopup(generalPopup.notificationId)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </UserNotificationContext.Provider>
  )
}

export const MaintenanceToolbarBanner: React.FC = () => {
  const { maintenance } = useUserNotifications()
  if (!maintenance) return null
  return (
    <div
      className='alert alert-warning py-2 px-3 mb-0 ms-auto text-start'
      style={{ width: '70%', maxWidth: 900, overflow: 'hidden' }}
    >
      <div className='fw-bold small'>{maintenance.title}</div>
      <div
        className='small'
        style={{ maxHeight: 48, overflow: 'hidden' }}
        dangerouslySetInnerHTML={{ __html: maintenance.messageHtml }}
      />
    </div>
  )
}
